// @file Surface ai chat store

import { trackEvent } from '@@/bits/analytics'
import environment from '@@/bits/environment'
import { EventSourceWithHeaders } from '@@/bits/event_source_with_headers'
import { __ } from '@@/bits/intl'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { safeLocalStorage } from '@@/bits/safe_storage'
import { magicTemplateEnum } from '@@/pinia/magic_padlet_panel_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import { useSurfaceOnboardingPanelStore } from '@@/pinia/surface_onboarding_panel'
import { useSurfaceStartingStateStore } from '@@/pinia/surface_starting_state'
import PadletApi from '@@/surface/padlet_api'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export enum Role {
  USER = 'user',
  ASSISTANT = 'assistant',
  SYSTEM = 'system',
}

export interface Message {
  role: string
  content: string
}

export interface Suggestion {
  long_message: string
  short_message: string
}

export interface Assistant {
  id: string
  name: string
  instructions: string
  model: string
}

export interface SerializedWall {
  title: string
  subtitle: string
  sections: Array<{
    section_id: string
    section_title: string
  }>
  posts: Array<{
    post_id: string
    section_id: string
    subject: string
    body: string
  }>
}

export enum SuggestionsStatus {
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error',
}

export const useSurfaceAIChatStore = defineStore('surfaceAIChatStore', () => {
  const DEFAULT_SUGGESTIONS: Suggestion[] = [
    {
      short_message: __('What can you help me with?'),
      long_message: __('What can you help me with?'),
    },
    {
      short_message: __('Add posts about a topic of my choosing.'),
      long_message: __('Add posts about a topic of my choosing.'),
    },
    {
      short_message: __('How do I add custom fields to my posts?'),
      long_message: __('How do I add custom fields to my posts?'),
    },
  ]

  const surfaceOnboardingPanelStore = useSurfaceOnboardingPanelStore()
  const surfaceStore = useSurfaceStore()
  const surfaceStartingStateStore = useSurfaceStartingStateStore()
  const surfaceCurrentUserStore = useSurfaceCurrentUserStore()
  const xSurfaceAIChatPanel = ref(false)
  const magicWallFirstOpen = ref(false)
  const assistants = ref<Assistant[]>([])
  const wallContextMessage = ref<string | null>(null)
  const currentAssistant = ref<Assistant | null>(null)
  const messages = ref<Message[]>([])
  const threadId = ref<string | null>(null)
  const source = ref<EventSourceWithHeaders | null>(null)

  const suggestions = ref<Suggestion[]>(DEFAULT_SUGGESTIONS)
  const suggestionsStatus = ref<SuggestionsStatus>(SuggestionsStatus.SUCCESS)

  const isLoadingSuggestions = computed(() => suggestionsStatus.value === SuggestionsStatus.LOADING)

  const baseUrl = environment === 'development' ? 'https://morpheus.padlet.dev' : 'https://morpheus.padlet.com'

  const isEventStreamOpen = computed(() => source.value !== null)
  const hasMessages = computed(() => messages.value.length > 0)
  const isLoadingResponse = ref(false)

  const hasFinishedResponse = computed(() => {
    return !isEventStreamOpen.value && !isLoadingResponse.value
  })

  async function loadMagicWallFirstOpen(): Promise<void> {
    // Create assistant options
    const jsonFormData = safeLocalStorage.getItem('magicWallOptions')
    if (jsonFormData != null) {
      magicWallFirstOpen.value = true
      // TODO ENG-16658: Save options somewhere to use in first chat
      wallContextMessage.value = jsonFormData
      safeLocalStorage.removeItem('magicWallOptions')
    }
  }

  async function initializeAssistants(): Promise<void> {
    assistants.value = await PadletApi.SurfaceAIChat.getAssistants()

    if (assistants.value.length > 0) {
      currentAssistant.value = assistants.value[0]
    }
  }

  async function initializeEventSource(assistantId: string, wallId: number, threadId: string): Promise<void> {
    source.value = new EventSourceWithHeaders(
      `${baseUrl}/api/v1/assistant/${assistantId}/${wallId}/threads/${threadId}/event-stream`,
      {
        Authorization: `Bearer ${useSurfaceStartingStateStore().morpheusToken}`,
      },
    )
    source.value.addEventListener('message', (event) => {
      const data = JSON.parse(event.data)
      if (data.event === 'thread.message.created') {
        isLoadingResponse.value = false
      }
      if (data.event === 'thread.message.delta') {
        const messageDelta = data.data.delta.content[0].text.value
        addAssistantMessageDelta(messageDelta)
      }
      if (data.event === 'thread.message.completed') {
        // Log assistant response
        const lastMessage = messages.value[messages.value.length - 1]
        if (lastMessage.role === Role.ASSISTANT) {
          trackEvent('SurfaceAiChat', 'AssistantResponse', lastMessage.content, lastMessage.content.length, {
            message_length: lastMessage.content.length,
            wall_id: wallId,
            thread_id: threadId,
            message_history: messages.value,
          })
        }

        // TODO: Figure out how to leave empty line in div. Chaining \n doesn't work with markdown-it
        addAssistantMessageDelta('\n\n')
      }
      if (
        data.event === 'thread.run.completed' ||
        data.event === 'thread.run.failed' ||
        data.event === 'thread.run.expired' ||
        data.event === 'thread.run.cancelled'
      ) {
        closeEventSource()
      }
    })
    source.value.addEventListener('error', (event) => {
      closeEventSource()
    })
    await source.value.connect()
  }

  function closeEventSource(): void {
    if (source.value != null) {
      source.value.close()
      source.value = null
    }
  }

  async function fetchSuggestions(): Promise<any> {
    try {
      suggestionsStatus.value = SuggestionsStatus.LOADING
      const serializedWallResponse = await PadletApi.SurfaceAIChat.getSerializedWall(surfaceStore.wallId)
      const serializedWall: SerializedWall = serializedWallResponse.data.attributes
      const response = await PadletApi.SurfaceAIChat.getSuggestions(
        asciiSafeStringify(serializedWall),
        surfaceStartingStateStore.currentCountry ?? '',
        surfaceCurrentUserStore.currentUser?.lang ?? '',
        surfaceCurrentUserStore.currentUser?.account_type ?? '',
      )
      suggestions.value = response.suggestions
      suggestionsStatus.value = SuggestionsStatus.SUCCESS
    } catch {
      suggestions.value = DEFAULT_SUGGESTIONS
      suggestionsStatus.value = SuggestionsStatus.ERROR
    }
  }

  async function onMessage(message: Message): Promise<void> {
    pushUserMessage(message.content)
    trackEvent('SurfaceAiChat', 'UserMessage', message.content, message.content.length, {
      message_length: message.content.length,
      wall_id: surfaceStore.wallId,
      thread_id: threadId.value,
    })

    pushAssistantMessage('')
    isLoadingResponse.value = true
    // Create thread and send pre message.
    if (threadId.value === null) {
      const response = await PadletApi.SurfaceAIChat.getMessageThread(surfaceStore.wallId)
      threadId.value = response.thread_id
      await PadletApi.SurfaceAIChat.sendMessage(threadId.value as string, {
        role: Role.SYSTEM,
        content: constructPreMessage(),
      })
    }
    // Send message to assistant
    // Need to await here to ensure message is inside thread
    const serializedWallResponse = await PadletApi.SurfaceAIChat.getSerializedWall(surfaceStore.wallId)
    const serializedWall: SerializedWall = serializedWallResponse.data.attributes
    message.content = constructFullMessage(message.content, serializedWall)
    await PadletApi.SurfaceAIChat.sendMessage(threadId.value as string, message)
    if (currentAssistant.value !== null && threadId.value !== null) {
      await initializeEventSource(currentAssistant.value.id, surfaceStore.wallId, threadId.value)
    }
  }

  function getNameByKey(key: string): string {
    for (const template in magicTemplateEnum) {
      if (magicTemplateEnum[template].key === key) {
        return magicTemplateEnum[template].name
      }
    }
    return 'board'
  }

  function constructPreMessage(): string {
    const lang = surfaceCurrentUserStore.currentUser?.lang
    const country = surfaceStartingStateStore.currentCountry
    const accountType = surfaceCurrentUserStore.currentUser?.account_type

    let preMessage = '# User context #\n'
    if (lang != null) {
      preMessage += `Preferred language: ${lang}\n`
    }

    if (country != null) {
      preMessage += `Current location: ${country}\n`
    }

    if (accountType != null) {
      preMessage += `Account type: ${accountType}\n`
    }

    if (wallContextMessage.value != null) {
      preMessage += `${formatMagicWallOptions(JSON.parse(wallContextMessage.value))}\n`
    }
    return preMessage.trim()
  }

  function constructFullMessage(message: string, serializedWall: SerializedWall): string {
    return `
        # padlet contents #
        ${asciiSafeStringify(serializedWall)}

        # user message #
        ${message}
    `
  }

  function resetMessages(): void {
    threadId.value = null
    messages.value = []
  }

  function addAssistantMessageDelta(delta: string): void {
    messages.value[messages.value.length - 1].content += delta
  }

  function pushAssistantMessage(content: string): void {
    messages.value.push({
      role: Role.ASSISTANT,
      content,
    })
  }

  function pushUserMessage(content: string): void {
    messages.value.push({
      role: Role.USER,
      content,
    })
  }

  function setCurrentAssistant(assistantId: string): void {
    currentAssistant.value = assistants.value?.find((assistant) => assistant.id === assistantId) ?? null
  }

  function showChatPanel(): void {
    xSurfaceAIChatPanel.value = true
  }

  /**
   * Transform object keys and convert the object to AI/human readable string
   * @example
   * input: { student_grade: 5 }; output: 'Student Grade : 5'
   */
  function formatMagicWallOptions(json): string {
    const formattedLines = Object.entries(json)
      .filter(([key, value]) => !['wallType', 'isExample', 'includeImages'].includes(key) && value) // Remove specific keys and empty values
      .map(([key, value]) => {
        const formattedKey = key
          .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space between camelCase words
          .replace(/_/g, ' ') // Replace underscores with spaces
          .replace(/\b\w/g, (char) => char.toUpperCase()) // Capitalize each word
        return `${formattedKey}: "${value as string}"`
      })
    const padletContext = `
# initial padlet context #
This padlet was just created by an LLM based on user input.
The user requested a ${getNameByKey(surfaceStore.magicWallType)} with the following parameters: \n
`
    return `${padletContext}${formattedLines.join('\n')}`
  }

  function toggleChatPanel(): void {
    if (surfaceOnboardingPanelStore.showOnboardingPanel) {
      surfaceOnboardingPanelStore.closeOnboardingPanel()
    }
    xSurfaceAIChatPanel.value = !xSurfaceAIChatPanel.value
  }

  return {
    currentAssistant,
    assistants,
    suggestions,
    messages,
    threadId,
    hasMessages,
    onMessage,
    resetMessages,
    showChatPanel,
    toggleChatPanel,
    setCurrentAssistant,
    fetchSuggestions,
    xSurfaceAIChatPanel,
    magicWallFirstOpen,
    isEventStreamOpen,
    isLoadingSuggestions,
    isLoadingResponse,
    hasFinishedResponse,
    loadMagicWallFirstOpen,
    initializeAssistants,
    initializeEventSource,
    closeEventSource,
  }
})
